import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@pilot-hub/auth'
import { AppShell, NavIcons, NAV_ICON_CLASS, UserMenuIcons, StatusBar, StaleBanner, useFeedbackUngelesen, useMissionContext } from '@pilot-hub/ui'
import type { NavItem, UserMenuEntry, PaletteAction } from '@pilot-hub/ui'
import { missionApi, droneApi, akkuApi, userApi } from '@pilot-hub/api-client'
import { akkuAnzeige } from '../utils/akku'
import { isFeatureEnabled } from '@pilot-hub/config'


/**
 * Admin-Modul auf der gemeinsamen AppShell (packages/ui/src/components/shell).
 * Gruppen: Betrieb / Material / Verwaltung. Einstellungen und Feedback über Benutzermenü und Befehlspalette.
 */
export function Layout() {
  const { user, logout } = useAuth()
  const { activeMission } = useMissionContext()
  const location = useLocation()
  const feedbackUngelesen = useFeedbackUngelesen()

  const navigate = useNavigate()
  const darfVerwalten = !!user?.rollen?.some(r => r === 'Admin' || r === 'Führungskraft' || r === 'ADMIN' || r === 'FUEHRUNG')
  const istAdmin = !!user?.rollen?.some(r => r === 'Admin' || r === 'ADMIN')

  // Befehlspalette (Strg+K): „Neu …“ und „Einsatz öffnen: …“ — Navigationsmenü bleibt unverändert
  const [einsaetzeKurz, setEinsaetzeKurz] = useState<{ id: number; titel: string; einsatznummer?: string | null }[]>([])
  useEffect(() => {
    if (!darfVerwalten) return
    let weg = false
    missionApi.list({ size: 500 })
      .then((r: any) => { if (!weg) setEinsaetzeKurz(((r?.data?.items ?? r?.items ?? []) as any[]).slice(0, 60)) })
      .catch(() => { /* ohne Einsatzliste nur ohne „Einsatz öffnen“ */ })
    return () => { weg = true }
  }, [darfVerwalten])
  // Drohnen, Akkus, Benutzer für „… öffnen“ (Benutzer nur für Admins)
  const [drohnenKurz, setDrohnenKurz] = useState<{ id: number; label: string; suche: string }[]>([])
  const [akkusKurz, setAkkusKurz] = useState<{ id: number; label: string; suche: string }[]>([])
  const [nutzerKurz, setNutzerKurz] = useState<{ id: number; label: string }[]>([])
  const listeVon = (r: any): any[] => (r?.data?.items ?? r?.items ?? r?.data ?? []) as any[]
  useEffect(() => {
    if (!darfVerwalten) return
    let weg = false
    droneApi.list({ size: 500 }).then((r: any) => { if (!weg) setDrohnenKurz(listeVon(r).map(d => ({
      id: d.id, label: [d.asb_inventarnummer, [d.hersteller, d.modell].filter(Boolean).join(' ')].filter(Boolean).join(' – ') || d.bezeichnung || `Drohne #${d.id}`,
      // zusätzlich nach Kennzeichen, Seriennummer, Inventarnummer und Bezeichnung auffindbar
      suche: [d.kennzeichen, d.seriennummer, d.asb_inventarnummer, d.bezeichnung].filter(Boolean).join(' '),
    }))) }).catch(() => { /* ohne Liste nur ohne „Drohne öffnen“ */ })
    akkuApi.list({ size: 500 }).then((r: any) => { if (!weg) setAkkusKurz(listeVon(r).map(a => ({ id: a.id, label: akkuAnzeige(a) || `Akku #${a.id}`, suche: [a.seriennummer, a.dji_seriennummer, a.asb_inventarnummer, a.bezeichnung].filter(Boolean).join(' ') }))) }).catch(() => { /* s. o. */ })
    if (istAdmin) userApi.list({ size: 500 }).then((r: any) => { if (!weg) setNutzerKurz(listeVon(r).map(u => ({
      id: u.id, label: [u.vorname, u.nachname].filter(Boolean).join(' ') || u.benutzername,
    }))) }).catch(() => { /* s. o. */ })
    return () => { weg = true }
  }, [darfVerwalten, istAdmin])
  const paletteActions: PaletteAction[] = useMemo(() => [
    ...(darfVerwalten ? [
      { id: 'neu-drohne', label: 'Neue Drohne', group: 'Aktion', keywords: 'anlegen hinzufügen drohne', run: () => navigate('/drones?neu=1') },
      { id: 'neu-akku', label: 'Neuer Akku', group: 'Aktion', keywords: 'anlegen hinzufügen akku', run: () => navigate('/akkus?neu=1') },
      { id: 'neu-einsatz', label: 'Neuer Einsatz', group: 'Aktion', keywords: 'anlegen einsatz übung', run: () => navigate('/missions?neu=1') },
    ] : []),
    ...(istAdmin ? [{ id: 'neu-benutzer', label: 'Neuer Benutzer', group: 'Aktion', keywords: 'anlegen hinzufügen nutzer user', run: () => navigate('/users?neu=1') }] : []),
    ...einsaetzeKurz.map(e => ({
      id: `einsatz-${e.id}`, label: `Einsatz öffnen: ${e.titel}`, group: 'Einsatz', keywords: `${e.einsatznummer ?? ''} einsatz öffnen`,
      run: () => navigate(`/missions?oeffnen=${e.id}`),
    })),
    ...drohnenKurz.map(d => ({ id: `drohne-${d.id}`, label: `Drohne öffnen: ${d.label}`, group: 'Material', keywords: `drohne öffnen ${d.suche}`, run: () => navigate(`/drones?drohne=${d.id}`) })),
    ...akkusKurz.map(a => ({ id: `akku-${a.id}`, label: `Akku öffnen: ${a.label}`, group: 'Material', keywords: `akku öffnen ${a.suche}`, run: () => navigate(`/akkus?akku=${a.id}`) })),
    ...nutzerKurz.map(u => ({ id: `benutzer-${u.id}`, label: `Benutzer öffnen: ${u.label}`, group: 'Verwaltung', keywords: 'benutzer nutzer user öffnen', run: () => navigate(`/users?benutzer=${u.id}`) })),
  ], [darfVerwalten, istAdmin, einsaetzeKurz, drohnenKurz, akkusKurz, nutzerKurz, navigate])

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', group: 'Betrieb', dock: true, icon: <NavIcons.dashboard className={NAV_ICON_CLASS} /> },
    { path: '/missions', label: 'Einsätze', group: 'Betrieb', dock: true, icon: <NavIcons.missions className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'missions') },
    { path: '/flights', label: 'Flüge', group: 'Betrieb', dock: true, icon: <NavIcons.flights className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'flights') },
    { path: '/medien', label: 'Medien', group: 'Betrieb', icon: <NavIcons.media className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'] },
    { path: '/checklisten', label: 'Checklisten', group: 'Betrieb', icon: <NavIcons.checklist className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'checklisten') },
    { path: '/drones', label: 'Drohnen', group: 'Material', dock: true, icon: <NavIcons.drones className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'drones') },
    { path: '/akkus', label: 'Akkus', group: 'Material', icon: <NavIcons.akkus className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'akkus') },
    { path: '/vehicles', label: 'Fahrzeuge', group: 'Material', icon: <NavIcons.vehicles className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'vehicles') },
    { path: '/users', label: 'Benutzer', group: 'Verwaltung', icon: <NavIcons.users className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'users') },
    { path: '/licenses', label: 'Lizenzen', group: 'Verwaltung', icon: <NavIcons.licenses className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'licenses') },
    { path: '/export', label: 'Export', group: 'Verwaltung', icon: <NavIcons.export className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'export') },
  ]

  const userMenu: UserMenuEntry[] = useMemo(() => [
    { id: 'settings', label: 'Einstellungen', icon: UserMenuIcons.settings, to: '/settings' },
    {
      id: 'feedback', label: 'Feedback & Roadmap', icon: UserMenuIcons.feedback, to: '/feedback',
      state: { von: location.pathname + location.search }, badge: feedbackUngelesen,
    },
    { type: 'heading', label: 'Handbuch' },
    { id: 'hb-kompakt', label: 'Handbuch kompakt', icon: UserMenuIcons.handbookShort, href: '/handbuch/anwenderhandbuch-admin-kompakt.pdf' },
    { id: 'hb-lang', label: 'Handbuch ausführlich', icon: UserMenuIcons.handbookLong, href: '/handbuch/anwenderhandbuch-admin-ausfuehrlich.pdf' },
    { type: 'separator' },
    { id: 'logout', label: 'Abmelden', icon: UserMenuIcons.logout, danger: true, onSelect: () => logout() },
  ], [location.pathname, location.search, feedbackUngelesen, logout])

  return (
    <AppShell
      moduleName="Admin-Modul"
      moduleSubtitle="ASB Drohnenstaffel"
      logoSrc="/logo.png"
      navItems={navItems}
      user={{ name: `${user?.vorname ?? ''} ${user?.nachname ?? ''}`.trim() || 'Benutzer', id: user?.id, farbschema: (user as any)?.farbschema, username: user?.benutzername, subtitle: (user?.rollen?.[0] as string | undefined), dot: feedbackUngelesen > 0 }}
      userMenu={userMenu}
      paletteActions={paletteActions}
      status={<StatusBar />}
      banner={<StaleBanner />}
      headerLeft={activeMission ? (
        <div className="hidden items-center gap-2 rounded-full border border-primary-600/30 bg-primary-600/10 px-3 py-1 text-sm text-primary-200 md:flex">
          📍 {activeMission.title}
        </div>
      ) : undefined}
    />
  )
}
